import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import HorizontalDivider from "src/components/HorizontalDivider/HorizontalDivider";
import VerticalDivider from "src/components/VerticalDivider/VerticalDivider";
import { isInvoiceNotPaid, isInvoicePaid, isInvoicePGW } from "src/utils/common";
import { convertDateIFNotMin, getTextValueById } from "src/utils/helpers";

const InvoiceDetailsDefaultView = () => {
  const { t } = useTranslation(["common", "registration", "invoice"]);
  const invoiceStatusOptions = useSelector(
    (state: any) => state.lookup.invoiceStatusOptions
  );
  const invoiceDetails = useSelector(
    (state: any) => state.invoices?.invoiceDetailsPopupData?.invoiceDetails
  );
  const {
    propertyID,
    invoiceNumber,
    buildingName,
    totalAmountWithoutTax,
    totalAmountWithTax,
    invoiceLineItems,
    paymentDate,
    paymentDueDate,
    totalTax,
    paymentStatus,
    contractNumber,
  } = invoiceDetails;
  const systems = useSelector((state: any) => state.lookup.systems);
  let paymentStatusClass;
  const isNotPaid = isInvoiceNotPaid(paymentStatus);
  const isPaid = isInvoicePaid(paymentStatus);
  const isPending = paymentStatus == 0 ? false : isInvoicePGW(paymentStatus);
  if (isNotPaid) {
    paymentStatusClass = "not-paid";
  } else if (isPaid) {
    paymentStatusClass = "completed";
  } else if (isPending) {
    paymentStatusClass = "in-progress";
  }
  else // refunded
  {
    paymentStatusClass = "refunded";
  }
  const invoiceTotals = [
    {
      label: t("invoiceDetails.totalAmountWithoutTax"),
      value: totalAmountWithoutTax,
    },
    { label: t("invoiceDetails.totalTax"), value: totalTax },
    {
      label: t("invoiceDetails.totalAmountWithTax"),
      value: totalAmountWithTax,
    },
  ];

  return (
    <div className="invoice-details flex-column default-view">
      <section className="invoice-header mb-0">
        <div className="invoice-header__details">
          <div className="invoice-header__details__info d-flex">
            <div className="invoice-header__details__info__row top-column d-flex justify-content-start flex-column w-40">
              <div className="invoice-header__details__info__row__item d-flex  align-items-center justify-content-start pe-2 py-2">
                <h6 className="w-50">{t("invoiceDetails.invoiceNumber")}</h6>
                <span className="w-50">{invoiceNumber}</span>
              </div>
              <div className="invoice-header__details__info__row__item d-flex align-items-center justify-content-start pe-2 py-2">
                <h6 className="w-50">{t("invoiceDetails.propertyNumber")}</h6>
                <span className="w-50">{propertyID}</span>
              </div>
              <div className="invoice-header__details__info__row__item left-item d-flex align-items-center justify-content-start pe-2 py-2">
                <h6 className="w-50">{t("contractNo")}</h6>
                <span className="plain-text-date w-50">{contractNumber}</span>
              </div>
            </div>
            <div className="invoice-header__details__info__row top-column d-flex justify-content-start flex-column w-30">
              <div className="invoice-header__details__info__row__item left-item d-flex align-items-center justify-content-start pe-2 py-2">
                <h6 className="w-50">{t("invoiceDetails.invoiceDueDate")}</h6>
                <span className="plain-text-date w-50">
                  {convertDateIFNotMin(paymentDueDate)}
                </span>
              </div>
              <div className="invoice-header__details__info__row__item d-flex  align-items-center justify-content-start pe-2 py-2">
                <h6 className="w-50">{t("invoiceDetails.propertyName")}</h6>
                <span className="w-50">{buildingName}</span>
              </div>
              {paymentDate && (
                <div className="invoice-header__details__info__row__item left-item d-flex align-items-center justify-content-start pe-2 py-2">
                  <h6 className="w-50">{t("invoiceDetails.paymentDate")}</h6>
                  <span className="plain-text-date w-50">
                    {convertDateIFNotMin(paymentDate)}
                  </span>
                </div>
              )}
            </div>
            <div className="invoice-header__details__info__row top-column d-flex justify-content-start flex-column w-30">
              <div className="invoice-header__details__info__row__item left-item d-flex align-items-center justify-content-start pe-2 pb-2 mt-1">
                <h6 className="w-50">{t("invoiceStatus")}</h6>
                <span className={`fit-content oval ${paymentStatusClass}`}>
                  {getTextValueById(paymentStatus, invoiceStatusOptions, "id")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="invoice-body invoice-amount mt-1">
        <div className="invoice-body__sub-title py-1 px-2 d-flex justify-content-between align-items-center ">
          <span className="w-70">{t("invoiceDetails.item")}</span>
          <span className="w-29 text-center">
            {t("invoiceDetails.feesAED")}
          </span>
        </div>
        <div className="invoice-header__details__info d-flex flex-column">
          {invoiceLineItems?.map((invoiceItem: any, index: number) => (
            <>
              <div className="invoice-header__details__info__row d-flex justify-content-between px-2">
                <div className="invoice-header__details__info__row__item d-flex align-items-start justify-content-between pt-2 w-70">
                  <h6>
                    {getTextValueById(
                      invoiceItem?.systemServiceId,
                      systems as unknown as [],
                      "systemServiceId"
                    )}
                  </h6>
                </div>
                <VerticalDivider />
                <div className="invoice-header__details__info__row__item d-flex align-items-start justify-content-center pt-2 w-29">
                  <span>{invoiceItem.totalAmount}</span>
                </div>
              </div>
              <HorizontalDivider />
            </>
          ))}
          {invoiceTotals.map((item, index) => (
            <React.Fragment key={item.label}>
              <div
                className={`invoice-header__details__info__row d-flex justify-content-between px-2 total ${index === invoiceTotals.length - 1 ? "total-with-tax" : ""
                  }`}
              >
                <div className="invoice-header__details__info__row__item d-flex align-items-center justify-content-end  w-70">
                  <h6 className="w-30 text-start px-2">{item.label}</h6>
                </div>
                <VerticalDivider />
                <div className="invoice-header__details__info__row__item d-flex align-items-start justify-content-center w-29">
                  <span>{item.value}</span>
                </div>
              </div>
              <HorizontalDivider />
            </React.Fragment>
          ))}
        </div>
        {isPending && (
          <h4 className="locked-invoice text-center mt-2">{t("lockedInvoice")}</h4>
        )}
      </section>
    </div>
  );
};

export default InvoiceDetailsDefaultView;
